/** Components for `./documentTitle`, split out so that module can stay a plain `.ts`. */
import * as React from "react";
import { useRouterState } from "@tanstack/react-router";
import { useDashboardBrand } from "./DashboardBrand";
import {
  pageTitleFor,
  composeTitle,
  SubjectContext,
  type SubjectSlot,
} from "./documentTitle";

/**
 * Keeps `document.title` in step with the route and the current subject.
 *
 * Renders its children untouched — it is a behaviour, not a layout.
 */
export function DocumentTitleProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const brand = useDashboardBrand();
  const [subject, setSubject] = React.useState<string | undefined>(undefined);
  // The deepest matched route that names a page: a detail route wins over the
  // layout route it is nested in.
  const page = useRouterState({
    select: (state) => {
      for (let i = state.matches.length - 1; i >= 0; i -= 1) {
        const title = pageTitleFor(state.matches[i]?.routeId as string | undefined);
        if (title) return title;
      }
      return undefined;
    },
  });

  const slot = React.useMemo<SubjectSlot>(() => ({ setSubject }), []);

  React.useEffect(() => {
    document.title = composeTitle(page, subject, brand.name);
  }, [page, subject, brand.name]);

  return <SubjectContext.Provider value={slot}>{children}</SubjectContext.Provider>;
}
